import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/services/api';
import {
  Plus,
  FileText,
  ChevronRight,
  ChevronDown,
  Trash2,
  Edit3,
  Save,
  X,
  Search,
} from 'lucide-react';
import clsx from 'clsx';

const PAGE_ICONS = ['📄', '📋', '📖', '📝', '📁', '🏗️', '💡', '🎯', '📊', '🔧'];

export function Pages() {
  const { workspaceId, projectId } = useParams<{ workspaceId: string; projectId: string }>();
  const api = useApi();
  const queryClient = useQueryClient();

  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [editedTitle, setEditedTitle] = useState('');
  const [newPageName, setNewPageName] = useState('');
  const [newPageIcon, setNewPageIcon] = useState('📄');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedPages, setExpandedPages] = useState<Set<string>>(new Set());

  const { data: pages, isLoading } = useQuery({
    queryKey: ['pages', projectId],
    queryFn: () => api.getPages(projectId!),
    enabled: !!projectId,
  });

  const { data: members } = useQuery({
    queryKey: ['members', workspaceId],
    queryFn: () => api.getMembers(workspaceId!),
    enabled: !!workspaceId,
  });

  const createPageMutation = useMutation({
    mutationFn: (data: any) => api.createPage(data),
    onSuccess: (page) => {
      queryClient.invalidateQueries({ queryKey: ['pages', projectId] });
      setIsCreating(false);
      setNewPageName('');
      setNewPageIcon('📄');
      setSelectedPageId(page.bh_pageid);
    },
  });

  const updatePageMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => api.updatePage(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages', projectId] });
      setIsEditing(false);
    },
  });

  const deletePageMutation = useMutation({
    mutationFn: (id: string) => api.deletePage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages', projectId] });
      setSelectedPageId(null);
    },
  });

  const selectedPage = pages?.find((p: any) => p.bh_pageid === selectedPageId);
  const rootPages = pages?.filter((p: any) => !p.bh_parent) || [];
  const getChildPages = (parentId: string) => pages?.filter((p: any) => p.bh_parent === parentId) || [];
  const getMemberName = (memberId: string) => members?.find((m: any) => m.bh_memberid === memberId)?.bh_name || 'Unknown';

  const filteredPages = searchQuery
    ? pages?.filter((p: any) =>
        p.bh_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.bh_content?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : null;

  const toggleExpanded = (pageId: string) => {
    setExpandedPages(prev => {
      const next = new Set(prev);
      if (next.has(pageId)) next.delete(pageId);
      else next.add(pageId);
      return next;
    });
  };

  const handleCreatePage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPageName.trim()) return;
    createPageMutation.mutate({
      bh_name: newPageName.trim(),
      bh_icon: newPageIcon,
      bh_project: projectId,
      bh_content: '# ' + newPageName.trim() + '\n\nStart writing here...',
    });
  };

  const handleSave = () => {
    if (!selectedPageId) return;
    updatePageMutation.mutate({
      id: selectedPageId,
      data: {
        bh_name: editedTitle,
        bh_content: editedContent,
      },
    });
  };

  const startEditing = () => {
    if (selectedPage) {
      setEditedTitle(selectedPage.bh_name);
      setEditedContent(selectedPage.bh_content || '');
      setIsEditing(true);
    }
  };

  const renderPageItem = (page: any, depth = 0) => {
    const children = getChildPages(page.bh_pageid);
    const hasChildren = children.length > 0;
    const isExpanded = expandedPages.has(page.bh_pageid);
    const isSelected = selectedPageId === page.bh_pageid;

    return (
      <div key={page.bh_pageid}>
        <button
          onClick={() => setSelectedPageId(page.bh_pageid)}
          className={clsx(
            'w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors',
            isSelected ? 'bg-primary-50 text-primary-700' : 'hover:bg-gray-100 text-gray-700'
          )}
          style={{ paddingLeft: `${12 + depth * 16}px` }}
        >
          {hasChildren ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleExpanded(page.bh_pageid);
              }}
              className="p-0.5 hover:bg-gray-200 rounded"
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
          ) : (
            <span className="w-5" />
          )}
          <span>{page.bh_icon}</span>
          <span className="flex-1 text-left truncate">{page.bh_name}</span>
        </button>
        {hasChildren && isExpanded && (
          <div>
            {children.map((child: any) => renderPageItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex">
      {/* Sidebar */}
      <div className="w-72 border-r border-gray-200 bg-white flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-lg font-semibold text-gray-900">Pages</h1>
            <button
              onClick={() => setIsCreating(true)}
              className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search pages..."
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Create Page Form */}
        {isCreating && (
          <form onSubmit={handleCreatePage} className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center gap-2 mb-3">
              <select
                value={newPageIcon}
                onChange={(e) => setNewPageIcon(e.target.value)}
                className="px-2 py-1 border border-gray-200 rounded text-lg"
              >
                {PAGE_ICONS.map(icon => (
                  <option key={icon} value={icon}>{icon}</option>
                ))}
              </select>
              <input
                type="text"
                value={newPageName}
                onChange={(e) => setNewPageName(e.target.value)}
                placeholder="Page title"
                className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                autoFocus
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={createPageMutation.isPending}
                className="px-3 py-1.5 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-50"
              >
                Create
              </button>
              <button
                type="button"
                onClick={() => setIsCreating(false)}
                className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Page List */}
        <div className="flex-1 overflow-y-auto p-2">
          {searchQuery && filteredPages ? (
            filteredPages.length > 0 ? (
              filteredPages.map((page: any) => (
                <button
                  key={page.bh_pageid}
                  onClick={() => {
                    setSelectedPageId(page.bh_pageid);
                    setSearchQuery('');
                  }}
                  className={clsx(
                    'w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors',
                    'hover:bg-gray-100 text-gray-700'
                  )}
                >
                  <span>{page.bh_icon}</span>
                  <span className="flex-1 text-left truncate">{page.bh_name}</span>
                </button>
              ))
            ) : (
              <p className="text-center text-gray-500 py-4 text-sm">No pages found</p>
            )
          ) : rootPages.length > 0 ? (
            rootPages.map((page: any) => renderPageItem(page))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">No pages yet</p>
              <button
                onClick={() => setIsCreating(true)}
                className="mt-2 text-primary-600 hover:text-primary-700 text-sm"
              >
                Create your first page
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 flex flex-col bg-white">
        {selectedPage ? (
          <>
            {/* Page Header */}
            <div className="h-14 border-b border-gray-200 flex items-center justify-between px-6">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{selectedPage.bh_icon}</span>
                {isEditing ? (
                  <input
                    type="text"
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    className="text-lg font-medium text-gray-900 border-b-2 border-primary-500 focus:outline-none"
                  />
                ) : (
                  <h2 className="text-lg font-medium text-gray-900">{selectedPage.bh_name}</h2>
                )}
              </div>
              <div className="flex items-center gap-2">
                {isEditing ? (
                  <>
                    <button
                      onClick={handleSave}
                      disabled={updatePageMutation.isPending}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700"
                    >
                      <Save className="w-4 h-4" />
                      Save
                    </button>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg text-sm"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={startEditing}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg text-sm"
                    >
                      <Edit3 className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        if (confirm('Delete this page?')) {
                          deletePageMutation.mutate(selectedPage.bh_pageid);
                        }
                      }}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Page Content */}
            <div className="flex-1 overflow-y-auto p-8">
              <div className="max-w-3xl mx-auto">
                {isEditing ? (
                  <textarea
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    className="w-full h-[calc(100vh-250px)] p-4 border border-gray-200 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                    placeholder="Write your content in Markdown..."
                  />
                ) : (
                  <div className="prose prose-gray max-w-none">
                    {/* Simple markdown rendering */}
                    {selectedPage.bh_content?.split('\n').map((line: string, i: number) => {
                      if (line.startsWith('# ')) {
                        return <h1 key={i} className="text-3xl font-bold mb-4">{line.slice(2)}</h1>;
                      } else if (line.startsWith('## ')) {
                        return <h2 key={i} className="text-2xl font-semibold mt-6 mb-3">{line.slice(3)}</h2>;
                      } else if (line.startsWith('### ')) {
                        return <h3 key={i} className="text-xl font-medium mt-4 mb-2">{line.slice(4)}</h3>;
                      } else if (line.startsWith('- ')) {
                        return <li key={i} className="ml-4">{line.slice(2)}</li>;
                      } else if (line.trim() === '') {
                        return <br key={i} />;
                      } else {
                        return <p key={i} className="mb-2">{line}</p>;
                      }
                    })}
                  </div>
                )}

                {/* Page Meta */}
                <div className="mt-8 pt-6 border-t border-gray-200 text-sm text-gray-500">
                  <p>Created by {getMemberName(selectedPage.bh_createdby)} on {new Date(selectedPage.createdon).toLocaleDateString()}</p>
                  <p>Last modified {new Date(selectedPage.modifiedon).toLocaleDateString()}</p>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg">Select a page to view</p>
              <p className="text-sm mt-1">or create a new one</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
